import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { Audience } from '@dmn/shared';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { ReportingView } from '@/components/ReportingView';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { portalApi } from '@/lib/api';
import { queryKeys } from '@/lib/queries';
import { useSplitReporting } from '@/lib/useSplitReporting';
import { daysAgoUtc, formatCount, todayUtc } from '@/lib/utils';

export const Route = createFileRoute('/portal/reporting')({ component: PortalReporting });

const RANGES = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
] as const;

const ALL_CAMPAIGNS = 'all';

/** Same tab split as the admin console — see `reporting.index.tsx`. */
const TABS = [
  { audience: 'patient' as const, label: 'Patients', short: 'DTC' },
  { audience: 'hcp' as const, label: 'Practices', short: 'HCP' },
];

/**
 * The same `ReportingView` the admin console renders — see its docblock. No
 * "Refresh rollups" button here: that runs the aggregation job on demand,
 * which is an operator action, not a sponsor's to trigger.
 *
 * Tabbed on the same axis, so a sponsor running both patient and practice
 * campaigns reads the same separation their DearDoc contact does. A sponsor
 * running only one still sees both tabs, with the empty one's zero on the label —
 * hiding it would make the page's shape depend on the date range, and "we have
 * no HCP delivery" is itself an answer.
 */
function PortalReporting() {
  const [days, setDays] = useState('30');
  const [audience, setAudience] = useState<Audience>('patient');
  const [campaignId, setCampaignId] = useState<string>(ALL_CAMPAIGNS);

  /**
   * One flag governs both surfaces. A sponsor seeing tabs while their DearDoc
   * contact sees a blended table — or the reverse — would make the two
   * impossible to talk about, which is the whole reason `loadReporting` is one
   * implementation.
   *
   * `/v1/console/me` is reachable here: it is the one route behind `requireUser`
   * rather than `requireAdmin`, precisely so a sponsor can ask who they are.
   */
  const split = useSplitReporting();

  const from = daysAgoUtc(Number(days) - 1);
  const to = todayUtc();

  const campaigns = useQuery({ queryKey: queryKeys.portalCampaigns, queryFn: portalApi.campaigns });

  /**
   * Only this sponsor's campaigns that can deliver on the active tab. The portal
   * projection carries each placement's audience for exactly this.
   */
  const tabCampaigns = useMemo(
    () =>
      // Unsplit, there is no tab to scope to and every campaign belongs.
      split === true
        ? (campaigns.data?.campaigns ?? []).filter((campaign) =>
            campaign.placements.some((placement) => placement.audience === audience)
          )
        : (campaigns.data?.campaigns ?? []),
    [campaigns.data, audience, split]
  );

  const selectable = tabCampaigns.some((campaign) => campaign.id === campaignId);
  const scoped = campaignId === ALL_CAMPAIGNS || !selectable ? undefined : campaignId;

  const patient = useQuery({
    queryKey: queryKeys.portalReporting(
      from,
      to,
      audience === 'patient' ? scoped : undefined,
      'patient'
    ),
    queryFn: () =>
      portalApi.reporting(from, to, audience === 'patient' ? scoped : undefined, 'patient'),
    enabled: split === true,
  });
  const hcp = useQuery({
    queryKey: queryKeys.portalReporting(from, to, audience === 'hcp' ? scoped : undefined, 'hcp'),
    queryFn: () => portalApi.reporting(from, to, audience === 'hcp' ? scoped : undefined, 'hcp'),
    enabled: split === true,
  });

  /** The pre-tabs view: both audiences in one table. */
  const blended = useQuery({
    queryKey: queryKeys.portalReporting(from, to, scoped, 'all'),
    queryFn: () => portalApi.reporting(from, to, scoped), // no audience param: the server reports both
    enabled: split === false,
  });

  const byAudience = { patient, hcp } as const;

  return (
    <>
      <PageHeader
        title="Reporting"
        description="Aggregated from rollups — never from raw per-person events"
        actions={
          <>
            <Select
              value={selectable ? campaignId : ALL_CAMPAIGNS}
              onValueChange={setCampaignId}
              disabled={tabCampaigns.length === 0}
            >
              <SelectTrigger className="h-8 w-[210px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_CAMPAIGNS}>All campaigns</SelectItem>
                {tabCampaigns.map((campaign) => (
                  <SelectItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="h-8 w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RANGES.map((range) => (
                  <SelectItem key={range.value} value={range.value}>
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
      />
      <PageBody>
        {split !== true && (
          <ReportingView
            showVerticals={false}
            summary={blended.data}
            isPending={split === null || blended.isPending}
            isError={blended.isError}
            error={blended.error}
            onRetry={() => void blended.refetch()}
            audience="all"
          />
        )}

        {split === true && (
          <Tabs value={audience} onValueChange={(next) => setAudience(next as Audience)}>
            <TabsList>
              {TABS.map((tab) => {
                const query = byAudience[tab.audience];
                return (
                  <TabsTrigger key={tab.audience} value={tab.audience}>
                    {tab.label} ({tab.short})
                    {!query.isPending && (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {formatCount(query.data?.totals.impressions ?? 0)}
                      </span>
                    )}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {TABS.map((tab) => {
              const query = byAudience[tab.audience];
              return (
                <TabsContent key={tab.audience} value={tab.audience} className="mt-4">
                  <ReportingView
                    showVerticals={false}
                    summary={query.data}
                    isPending={query.isPending}
                    isError={query.isError}
                    error={query.error}
                    onRetry={() => void query.refetch()}
                    audience={tab.audience}
                  />
                </TabsContent>
              );
            })}
          </Tabs>
        )}
      </PageBody>
    </>
  );
}
